import Hero from "../../components/Hero/Hero"
import QuickAccessToRights from "./components/QuickAccessToRights"
import FeaturedActivities from "./components/FeaturedActivities"
import Breadcrumbs from "../../components/Breadcrumbs/Breadcrumbs"
import { useAppDispatch, useAppSelector } from "../../redux/hooks"
import { useEffect, useRef, useState } from "react"
import { getMajorActivities } from "../../redux/slices/activities/activitiesThunk"
import { getDisclaimer, getSupportInitiative, getVideo } from "../../redux/slices/monitor/monitorThunk"
import { getRights } from "../../redux/slices/rights/rightsThunk"
import SEO from "../../components/SEO/SEO"
import { setSelectedPeriod } from "../../redux/slices/periods/periodsSlice"
import SupportInitiative from "./components/SupportInitiative"
import Recognitions from "./components/Recognitions"
import TransversalApproaches from "./components/TransversalApproaches"
import { getODS, getPriorityGroups } from "../../redux/slices/odsPriorityGroups/odsPriorityGroupsThunk"
import { EvaluatedRights } from "../../interfaces/Rights"
import CTASubscription from "../../components/Cards/CTASubscription"
import Subscription from "./components/Subscription"
import { getProfileTypes } from "../../redux/slices/subscription/subscriptionThunk"
import Researchs from "./components/Researchs"
import { getMajorResearchs } from "../../redux/slices/researchs/researchsThunk"
import ContactSection from "../../components/Common/ContactSection"
import { useEnsureValidPeriod } from "../../hooks/useEnsureValidPeriod"

const Index = () => {
    const dispatch = useAppDispatch();

    const { majorActivities } = useAppSelector(state => state?.activities);
    const { video, allies, disclaimer } = useAppSelector((state) => state.monitor);
    const { rights, loading } = useAppSelector((state) => state.rights);
    const { periods, lastPeriod } = useAppSelector((state) => state.periods);
    const { ods, priorityGroups } = useAppSelector((state) => state.ods_priorityGroups);
    const { profiles, profilesOptions } = useAppSelector((state) => state.subscription);
    const { majorResearchs } = useAppSelector((state) => state.researchs)

    const [isModalOpen, setIsModalOpen] = useState(false);

    const hasFetched = useRef(false);

    useEffect(() => {
        if (hasFetched.current) return;
        hasFetched.current = true;

        const fetchData = async () => {
            const promises = [];

            if (!majorActivities?.length) {
                promises.push(dispatch(getMajorActivities()));
            }
            if (!profiles) {
                promises.push(dispatch(getProfileTypes()));
            }
            if (!video) {
                promises.push(dispatch(getVideo()));
            }
            if (!allies) {
                promises.push(dispatch(getSupportInitiative()));
            }
            if (!ods) {
                promises.push(dispatch(getODS()));
            }
            if (!priorityGroups) {
                promises.push(dispatch(getPriorityGroups()));
            }
            if (!disclaimer) {
                promises.push(dispatch(getDisclaimer()));
            }
            if (!majorResearchs) {
                promises.push(dispatch(getMajorResearchs()));
            }

            if (promises.length > 0) {
                const results = await Promise.allSettled(promises);
                results.forEach(result => {
                    if (result.status === 'rejected') {
                        console.error('Error:', result.reason);
                    }
                });
            }
        };

        fetchData();
    }, [dispatch]);

    useEffect(() => {
        const shouldDispatch = () => {
            if (!rights || !lastPeriod) return true;

            return rights.some((r) => {
                const evals = (r as EvaluatedRights).evaluaciones;
                return !evals;
            });
        };

        if (loading === 'idle' && shouldDispatch() && lastPeriod) {
            dispatch(getRights(lastPeriod?.value));
        }
    }, [rights, lastPeriod, loading, dispatch]);

    useEffect(() => {
        const hasModalBeenShown = localStorage.getItem("modalShown");
        if (!hasModalBeenShown) {
            setIsModalOpen(true);
        }
    }, []);

    useEnsureValidPeriod(periods, lastPeriod);

    const handleModalClose = (isOpen: boolean | ((prevState: boolean) => boolean)) => {
        setIsModalOpen(isOpen);
        if (!isOpen) {
            localStorage.setItem("modalShown", "true");
        }
    };

    const handlePeriods = (period: string) => {
        if (!period) return
        const year = period.split(" ").pop() || '';
        dispatch(setSelectedPeriod({ period: year }))
        dispatch(getRights(year))
    }

    return (
        <>
            <SEO
                title='Monitor de derechos CABA'
                description="Monitor de derechos de la ciudad de Buenos Aires"
                type='website'
            />
            <CTASubscription />
            <Hero
                breadcrumbs={<Breadcrumbs />}
                title={"Monitor de Derechos"}
                paragraph="El Monitor de Derechos es una iniciativa de la Defensoría del Pueblo de la Ciudad Autónoma de Buenos Aires que presenta información cuali-cuantitativa sistematizada, para evaluar avances-retrocesos en el nivel de satisfacción de los derechos humanos, dentro del ámbito local."
                media={{ type: 'video', src: '', url: `${video?.link}`, alt: `${video?.titulo}`, className: '' }}
                layout="row"
                backgroundClassName={{ backgroundColor: '#E3DDD8' }}
                contentContainerClassName='xl:items-end flex-col xl:flex-row !py-[3.25rem]'
                containerHeightClassName='h-full'
                titleClassName='text-brand-green-20'
            />
            {
                rights && rights.length > 0
                    ?
                    <QuickAccessToRights
                        quickAccessToRights={rights ? rights : []}
                        periods={periods ?? []}
                        lastPeriodSelected={`Período ${lastPeriod?.value}`}
                        handleOnChangePeriods={handlePeriods}
                        selectedPeriod={lastPeriod ?? { id: 0, value: '', estado: 0 }}
                        loading={loading}
                        disclaimer={disclaimer ?? ''}
                        handleModalClose={handleModalClose}
                        isModalOpen={isModalOpen}
                    />
                    : null
            }
            <TransversalApproaches
                ods={ods ?? []}
                priorityGroups={priorityGroups ?? []}
                period={lastPeriod?.value ?? ''}
            />
            {
                majorResearchs && majorResearchs.length > 0 &&
                <Researchs researchs={majorResearchs} />
            }
            {
                majorActivities && majorActivities?.length > 0
                    ? <FeaturedActivities activities={majorActivities} />
                    : <></>
            }
            {
                allies && allies?.length > 0
                    ? <SupportInitiative allies={allies} />
                    : <></>
            }
            <ContactSection space="mt-0" />
            <Recognitions />
            <Subscription
                profiles={profilesOptions}
            />
        </>
    )
}

export default Index 